import uuid from 'uuid/v4';
import {
	forEach, find, reject, map, isUndefined, isNull, get as lodashGet,
} from 'lodash';
import moment from 'moment';

let instance = null;

export default class CacheSystem {
	static setup(...params) {
		instance = new CacheSystem(...params);
	}

	static getInstance() {
		return instance;
	}

	/**
	 * @param  {String} prefix [localStorage prefix]
	 * @param  {Object} maxAge [Max Age to be used on every cache stored, default: { minutes : 10 }]
	 */
	constructor(prefix = 'js_cache', maxAge = { minutes : 10 }) {
		this.prefix = prefix;
		this.maxAge = maxAge;

		// Clean expired items when module get instantiated
		this.cleanExpired();
	}

	set(key, value, options = {}) {
		// Clean expired items
		this.cleanExpired();

		let expiration = null;
		if (!isUndefined(options.maxAge) && !isNull(options.maxAge)) expiration = moment().add(options.maxAge);
		else if (!isNull(this.maxAge)) expiration = moment().add(this.maxAge);

		const content = {
			key,
			value,
			expiration : !isNull(expiration) ? expiration.format() : expiration,
			group      : options.group || null,
			created    : moment().format(),
		};

		const item = this.get(key);

		// Get/Generate item uuid
		const itemUUID = isUndefined(item) ? uuid() : item.uuid;

		// Add/Update the cache item on local storage
		localStorage.setItem(`${this.prefix}:${itemUUID}`, JSON.stringify(content));

		return itemUUID;
	}

	get(key) {
		const item = find(this.getItems(), ['content.key', key]);

		if (isUndefined(item)) return undefined;

		if (this.hasExpired(item.content.expiration)) {
			this.deleteItemByUUID(item.uuid);
			return undefined;
		}

		return item;
	}

	/**
	 *
	 * @param {String}  key     [Key for the record to be searched/created]
	 * @param {Function} promise [Promise used to get the data to be Stored in case doesn't exist]
	 * @param {Object}  options
	 */
	getOrUpdate(key, promise, options = { returnItemValue : true, setOptions : {} }) {
		const item = this.get(key);

		if (!isUndefined(item)) return Promise.resolve(!options.returnItemValue ? item : lodashGet(item, 'content.value', {}));

		return promise()
			.then((response) => {
				this.set(key, response, options.setOptions);
				const newItem = this.get(key);
				return Promise.resolve(!options.returnItemValue ? newItem : lodashGet(newItem, 'content.value', {}));
			});
	}

	delete(key) {
		const item = this.get(key);

		if (isUndefined(item)) return;

		this.deleteItemByUUID(item.uuid);
	}

	deleteItemsByGroup(group) {
		forEach(this.getItemsByGroup(group), (item) => this.deleteItemByUUID(item.uuid));
	}

	clear() {
		forEach(this.getItems(), (item) => this.deleteItemByUUID(item.uuid));
	}

	getItems() {
		return reject(map(Object.keys(localStorage), (key) => {
			if (key.indexOf(this.prefix) === -1) return undefined;

			return {
				content : JSON.parse(localStorage[key]),
				uuid    : key.replace(`${this.prefix}:`, ''),
			};
		}), (item) => isUndefined(item));
	}

	getItemsByGroup(group) {
		return reject(this.getItems(), (item) => item.content.group !== group);
	}

	deleteItemByUUID(_uuid) {
		localStorage.removeItem(`${this.prefix}:${_uuid}`);
	}

	hasExpired = (expiration) => !isNull(expiration) && moment().isAfter(moment(expiration))

	cleanExpired() {
		forEach(this.getItems(), (item) => {
			// Delete items that are expired
			if (this.hasExpired(item.content.expiration)) this.deleteItemByUUID(item.uuid);
		});
	}
}
